import React, { useState } from 'react';
import { Star, Send, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ReviewForm = () => {
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    reviewText: '',
    canPublish: 'yes'
  });

  const handleStarClick = (starRating) => {
    setRating(starRating);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      toast({
        title: "Rating Required",
        description: "Please select a star rating before submitting.",
        variant: "destructive"
      });
      return;
    }

    try {
      const reviewData = { ...formData, rating };
      const response = await axios.post(`${API}/review`, reviewData);
      
      setSubmitted(true);
      toast({
        title: "Thank You!",
        description: "Your review has been submitted successfully. We appreciate your feedback!",
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Error",
        description: "Failed to submit review. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50 flex items-center justify-center px-4 py-20">
        <Card className="max-w-2xl w-full shadow-2xl">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Thank You!</h1>
            <p className="text-lg text-gray-600 mb-8">
              Your review has been submitted successfully. We truly appreciate you taking the time to share your experience with ICT TechSolutions.
            </p>
            <p className="text-gray-600 mb-8">
              Your feedback helps us improve our services and helps other customers make informed decisions.
            </p>
            <div className="flex justify-center space-x-4">
              <a href="/" className="text-blue-600 hover:text-blue-700 font-medium">
                Return to Homepage
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-gray-50">
      {/* Header Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Star className="text-white" size={32} />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Share Your Experience
          </h1>
          <p className="text-xl text-gray-600">
            We'd love to hear about your experience with ICT TechSolutions. Your feedback helps us serve you better!
          </p>
        </div>
      </section>

      {/* Review Form Section */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Card className="shadow-2xl">
            <CardContent className="p-8 md:p-12">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Personal Information */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Information</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Smith"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number (Optional)</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="0401 024 062"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="service">Service Received *</Label>
                      <Select 
                        value={formData.service} 
                        onValueChange={(value) => setFormData({ ...formData, service: value })} 
                        required
                      >
                        <SelectTrigger id="service">
                          <SelectValue placeholder="Select service" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="computer-repair">Computer Repair</SelectItem>
                          <SelectItem value="laptop-repair">Laptop Repair</SelectItem>
                          <SelectItem value="virus-removal">Virus Removal</SelectItem>
                          <SelectItem value="data-recovery">Data Recovery</SelectItem>
                          <SelectItem value="microsoft-365">Microsoft 365 Setup</SelectItem>
                          <SelectItem value="azure-cloud">Azure Cloud Services</SelectItem>
                          <SelectItem value="google-workspace">Google Workspace</SelectItem>
                          <SelectItem value="network-setup">Network Setup</SelectItem>
                          <SelectItem value="server-admin">Server Administration</SelectItem>
                          <SelectItem value="other">Other IT Services</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Rating Section */}
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rating *</h2>
                  <p className="text-gray-600 mb-4">How would you rate your experience?</p>
                  <div className="flex items-center space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => handleStarClick(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        className="transition-transform hover:scale-110 focus:outline-none"
                      >
                        <Star
                          size={48}
                          className={`${
                            star <= (hoveredRating || rating)
                              ? 'text-yellow-400 fill-yellow-400'
                              : 'text-gray-300'
                          } transition-colors duration-200`}
                        />
                      </button>
                    ))}
                  </div>
                  {rating > 0 && (
                    <p className="text-sm text-gray-600 mt-3">
                      You selected: {rating} star{rating !== 1 ? 's' : ''}
                      {rating === 5 && ' - Excellent!'}
                      {rating === 4 && ' - Very Good!'}
                      {rating === 3 && ' - Good'}
                      {rating === 2 && ' - Fair'}
                      {rating === 1 && ' - Needs Improvement'}
                    </p>
                  )}
                </div>

                {/* Review Text */}
                <div className="space-y-2">
                  <Label htmlFor="review">Your Review *</Label>
                  <p className="text-sm text-gray-600">
                    Please share details about your experience with our service
                  </p>
                  <Textarea
                    id="review"
                    placeholder="I had a great experience with ICT TechSolutions. They were professional, quick, and solved my computer issues efficiently..."
                    rows={6}
                    value={formData.reviewText}
                    onChange={(e) => setFormData({ ...formData, reviewText: e.target.value })}
                    required
                    className="resize-none"
                  />
                  <p className="text-xs text-gray-500">
                    Minimum 20 characters
                  </p>
                </div>

                {/* Permission to Publish */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <Label htmlFor="canPublish" className="text-base font-semibold text-gray-900 mb-3 block">
                    Permission to Publish
                  </Label>
                  <p className="text-sm text-gray-600 mb-4">
                    May we publish your review on our website and marketing materials?
                  </p>
                  <Select 
                    value={formData.canPublish} 
                    onValueChange={(value) => setFormData({ ...formData, canPublish: value })}
                  >
                    <SelectTrigger id="canPublish">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes, you may publish my review</SelectItem>
                      <SelectItem value="no">No, keep my review private</SelectItem>
                      <SelectItem value="anonymous">Yes, but keep it anonymous</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 transition-all duration-200"
                  >
                    Submit Review
                    <Send className="ml-2" size={20} />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Privacy Note */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Your privacy is important to us. We will never share your information with third parties.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
